import { useCallback, useRef, useState } from "react"
import { FILE_MOVE_MIME_TYPE } from "@ciq/shared/pages/file_upload/constants"

/**
 * @typedef {Object} UseDragAndDropResult
 * @property {string|null} activeDropTargetId - ID of the building currently hovered during drag
 * @property {boolean} isDragging - Boolean indicating if a file-move drag is in progress
 * @property {(buildingId: string) => void} handleDragEnter - Call when drag enters a building drop target
 * @property {(buildingId: string) => void} handleDragLeave - Call when drag leaves a building drop target
 * @property {(event: DragEvent) => void} handleDragOver - Call on dragOver to allow drop
 * @property {(buildingId: string, event: DragEvent) => void} handleDrop - Call when files are dropped on a building
 */

/**
 * Hook managing drag-and-drop state for file-to-building moves.
 * Uses dragCounterRef Map to handle enter/leave events firing on child elements
 * (same pattern as FileDropZone.js in zoneUploads).
 *
 * @param {Object} params
 * @param {(buildingId: string, fileIds: string[]) => void} params.onDrop - Callback when files are dropped on a building
 * @returns {UseDragAndDropResult} Hook result with drag state and handler functions
 */
export function useDragAndDrop({ onDrop }) {
  const [activeDropTargetId, setActiveDropTargetId] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  // Map<buildingId, number> — tracks enter/leave count per target
  // to avoid spurious leave events from child elements
  const dragCounterRef = useRef(new Map())

  const handleDragEnter = useCallback((buildingId) => {
    const counters = dragCounterRef.current
    const count = (counters.get(buildingId) || 0) + 1
    counters.set(buildingId, count)
    setActiveDropTargetId(buildingId)
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((buildingId) => {
    const counters = dragCounterRef.current
    const count = (counters.get(buildingId) || 0) - 1
    counters.set(buildingId, Math.max(0, count))

    if (count <= 0) {
      // Read counter state synchronously, then call setters sequentially
      // (pure state updaters only per React 19)
      let anyActive = false
      for (const c of counters.values()) {
        if (c > 0) {
          anyActive = true
          break
        }
      }

      if (!anyActive) {
        setActiveDropTargetId(null)
      }
      setIsDragging(anyActive)
    }
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }, [])

  const handleDrop = useCallback((buildingId, e) => {
    e.preventDefault()
    dragCounterRef.current.clear()
    setActiveDropTargetId(null)
    setIsDragging(false)

    const raw = e.dataTransfer.getData(FILE_MOVE_MIME_TYPE)
    if (!raw) return

    try {
      const fileIds = JSON.parse(raw)
      if (Array.isArray(fileIds) && fileIds.length > 0 && fileIds.every(id => typeof id === "string")) {
        onDrop(buildingId, fileIds)
      }
    } catch {
      // Invalid JSON data — ignore silently
    }
  }, [onDrop])

  return {
    activeDropTargetId,
    isDragging,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  }
}
